import { getTruckPlateColorLabel } from "../../models/Truck";
import { Waybill } from "../../models/Waybill";

/**
 * 从未知值中安全取出非空字符串。
 */
const asString = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
};

/**
 * 从未知值中安全取出有限数字。
 */
const asNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/**
 * 解析账单的 pay_for 字段。
 * 后端会尝试将其解析为 waybill：失败时保留为字符串（充值/提现等场景，对业务无意义），
 * 成功时为与 waybill 接口一致的订单对象。
 *
 * @param payFor 原始 pay_for 值
 * @returns 解析成功返回 Waybill，否则返回 null
 */
export const parsePayForWaybill = (payFor: unknown): Waybill | null => {
  if (payFor === null || payFor === undefined) return null;
  // 解析失败时后端保留原始字符串，业务侧不展示
  if (typeof payFor === "string") return null;
  if (typeof payFor !== "object" || Array.isArray(payFor)) return null;

  const record = payFor as Record<string, unknown>;
  if (record.data !== undefined && typeof record.data === "object" && record.data !== null) {
    return parsePayForWaybill(record.data);
  }

  const id = asString(record.id ?? record._id);
  if (!id) return null;

  return {
    ...(record as unknown as Waybill),
    id,
  };
};

/**
 * 从账单记录中读取并解析 pay_for。
 *
 * @param record 账单记录
 */
export const getPayForWaybillFromRecord = (record: Record<string, unknown> | null | undefined): Waybill | null => {
  if (!record) return null;
  return parsePayForWaybill(record.pay_for ?? record.payFor);
};

/**
 * 读取 pay_for 原始字符串 ID（解析失败时后端保留的值）。
 * 充值/提现等场景对业务无意义，调用方需自行判断是否继续查询运单。
 *
 * @param record 账单记录
 */
export const getPayForRawId = (record: Record<string, unknown> | null | undefined): string => {
  if (!record) return "";
  const payFor = record.pay_for ?? record.payFor;
  return asString(payFor);
};

/**
 * 判断账单类型是否通常无法关联运单（充值、提现）。
 *
 * @param typeCode 账单类型 code
 */
export const isPayForMeaninglessBillType = (typeCode: number | null): boolean => {
  return typeCode === 3 || typeCode === 4;
};

/**
 * 拼接地址城市展示文案。
 */
export const formatWaybillCityRoute = (waybill: Waybill | null): string => {
  if (!waybill) return "";
  const from = asString(waybill.from?.city);
  const to = asString(waybill.to?.city);
  if (!from && !to) return "";
  return `${from || "-"} → ${to || "-"}`;
};

/**
 * 拼接司机展示文案。
 */
export const formatWaybillDriverText = (waybill: Waybill | null): string => {
  if (!waybill?.driver) return "";
  const name = asString(waybill.driver.name);
  const phone = asString(waybill.driver.phone);
  if (!name && !phone) return "";
  return phone ? `${name || "-"}（${phone}）` : name;
};

/**
 * 获取运单货物名称。
 */
export const getWaybillCargoName = (waybill: Waybill | null): string => {
  return asString(waybill?.cargo?.name);
};

/**
 * 获取运单车牌号。
 */
export const getWaybillTruckPlate = (waybill: Waybill | null): string => {
  return asString(waybill?.truck?.plate);
};

/**
 * 获取运单车长车型。
 */
export const getWaybillTruckType = (waybill: Waybill | null): string => {
  return asString(waybill?.truck?.type);
};

/**
 * 获取运单车牌颜色文案。
 */
export const getWaybillTruckPlateColorLabel = (waybill: Waybill | null): string => {
  const plateColor = asNumber(waybill?.truck?.plate_color);
  if (plateColor === null) return "";
  return getTruckPlateColorLabel(plateColor);
};

/**
 * 获取运单货主名称。
 */
export const getWaybillOwnerName = (waybill: Waybill | null): string => {
  return asString(waybill?.owner?.name);
};
